
/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Icon } from "@mui/material";
import { eraseRichText, getUUIDFromKey, shuffle } from 'utils/helpers';
import { useLocation } from 'react-router-dom';
import { Howl } from 'howler'
import './styles.css'
import linearGradient from 'assets/theme/functions/linearGradient';
import Logo from '../../../assets/images/logo.svg'
import { getCardCollectionById } from 'services/card-collection-services';
import { Answer, QuizDto } from 'types';
import MDInput from 'components/MDInput';
import { QuestionValidator } from 'components/QuestionValidator';
import { createUpdateScore, getScoreByCardCollectionId } from 'services/score-services';
import { v4 } from 'uuid';
import { useAppContext } from 'context/app-context';

const uuid = v4()
function ExerciseQuizzes(): JSX.Element {
  const [scorePk, setScorePk] = useState('')
  const [pk, setPk] = useState<string>('')
  const [quizzes, setQuizzes] = useState<QuizDto[]>([])
  const [subCategory, setSubCategory] = useState<string>('')
  const [quiz, setQuiz] = useState<string>('')
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [playingDescriptionSound, setPlayingDescriptionSound] = useState(false);
  const [playingAnswerSound, setPlayingAnswerSound] = useState(false);
  const [seeAnimation, setSeeAnimation] = useState<boolean>(true)
  const [seeAnswer, setSeeAnswer] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [descriptionSound, setDescriptionSound] = useState<Howl>({} as Howl)
  const [answerSound, setAnswerSound] = useState<Howl>({} as Howl)
  const [descriptionAudioFile, setDescriptionAudioFile] = useState('')
  const [descriptionImageFile, setDescriptionImageFile] = useState('')
  const [answerAudioFile, setAnswerAudioFile] = useState('')
  const [answerImageFile, setAnswerImageFile] = useState('')
  const [description, setDescription] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [answer, setAnswer] = useState<string>('')
  const [answers, setAnswers] = useState<Answer[]>([])
  const [givenAnswer, setGivenAnswer] = useState<string>('')
  const [score, setScore] = useState('000')
  const location = useLocation()


  useEffect(() => {
    if (location.hasOwnProperty('pathname')) {
      const list = location.pathname.split('/')
      const { [list.length - 1]: pathname } = list
      setPk(`QUIZ#${pathname}`)
    }
  }, [location])

  useEffect(() => {
    if (pk) {
      (async () => {
        const quizzes = await getCardCollectionById(getUUIDFromKey(pk))
        setQuizzes(shuffle(Object.entries(quizzes)
          .map(([_, value]) => Object.entries(value).filter(
            ([innerKey, innerValue]) => innerKey === 'quizzes'//@ts-ignore
              && innerValue.length))
          .filter(value => value.length).flat().map(value => value[1]).flat() as QuizDto[]
        ))
      })()
    }
  }, [pk])


  useEffect(() => {
    if (pk) {
      (async () => {
        const lastScore = await getScoreByCardCollectionId(getUUIDFromKey(pk))
        if (lastScore) {
          setScorePk(lastScore.PK)
          setAnswers(lastScore.answers)
        }
      })()
    }
  }, [pk])


  useEffect(() => {
    if (quizzes.length) {
      setAnswer(quizzes[currentIndex].answer)
      setTitle(quizzes[currentIndex].title)
      setDescription(quizzes[currentIndex].description)
      setDescriptionAudioFile(quizzes[currentIndex].descriptionAudio)
      setDescriptionImageFile(quizzes[currentIndex].descriptionImage)
      setAnswerAudioFile(quizzes[currentIndex].answerAudio)
      setAnswerImageFile(quizzes[currentIndex].answerImage)
      setSubCategory(quizzes[currentIndex].SK)
      setQuiz(quizzes[currentIndex].PK)
      setScore(String(answers.filter(answer => answer.correct).length).padStart(3, '0'))
      setDisabled(Boolean(answers.find(answer => answer.correct && answer.quizId === getUUIDFromKey(quizzes[currentIndex].PK))))
      if (Boolean(answers.find(answer => answer.correct && answer.quizId === getUUIDFromKey(quizzes[currentIndex].PK)))) {
        setGivenAnswer(answers.find(answer => answer.correct && answer.quizId === getUUIDFromKey(quizzes[currentIndex].PK)).answer)
      }
    }
  }, [quizzes, currentIndex, answers])

  const { handleMessage} = useAppContext()

  useEffect(() => {
    if (descriptionAudioFile)
      setDescriptionSound(new Howl({
        src: descriptionAudioFile,
        html5: true,
        onend: function () {
          setPlayingDescriptionSound(false)
        },
      }))

  }, [descriptionAudioFile])

  useEffect(() => {
    if (answerAudioFile) {
      setAnswerSound(new Howl({
        src: answerAudioFile,
        html5: true,
        onend: function () {
          setPlayingAnswerSound(false)
        },
      }))
    }
  }, [answerAudioFile])


  const handleDescriptionSound = (): void => {
    setPlayingDescriptionSound(!playingDescriptionSound)
    try {
      if (!playingDescriptionSound) {
        descriptionSound.play()
      } else {
        descriptionSound.stop()
      }
    } catch (error) {
      handleMessage("Der er ingen lyd til denne quiz", 'info')
    }
  }

  const handleAnswerSound = (): void => {
    setPlayingAnswerSound(!playingAnswerSound)
    try {
      if (!playingAnswerSound) {
        answerSound.play()
      } else {
        answerSound.stop()
      }
    } catch (error) {
      handleMessage("Der er ingen lyd til denne quiz", 'info')
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAnswer = () => {
    if (!givenAnswer) return
    if (eraseRichText(answer) === givenAnswer) {
      setScore(String(parseInt(score) + 1).padStart(3, '0'))
    }
  }

  const handleClickNavigateQuestions = (direction: string) => {
    if (direction === 'left') {
      setCurrentIndex(Math.max(0, currentIndex - 1))
    }
    else {
      setCurrentIndex(Math.min(quizzes.length - 1, currentIndex + 1))
    }
    setSeeAnswer(false)
    setGivenAnswer('')
    setSeeAnimation(true)
  }

  useEffect(() => {
    if (answers.length && seeAnswer) {
      (async () => {
        await createUpdateScore(getUUIDFromKey(scorePk) || uuid,
          {
            answers,
            score: Number(score),
            progress: 40,
            cardCollectionId: getUUIDFromKey(pk)
          })
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers])


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card >
        <MDBox p={3} lineHeight={1}>
          <MDBox
            display='flex'
            justifyContent='space-between'
          >
            <MDTypography variant="h5" fontWeight="medium">
              Play Cards
            </MDTypography>
            <MDTypography variant="h5" fontWeight="medium">
              Score: {score}
            </MDTypography>
          </MDBox>
          <Grid container justifyContent={'center'}>
            <Grid item sm={12} lg={6} >
              <MDBox
                justifyContent='space-between'
                alignItems='center'
                display='flex'
                flexDirection='column'>
                <MDBox mt={1} px={2} width={"100%"}>
                  <MDBox mt={1} px={2}
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                  >
                    <MDBox mt={1}>
                      <MDBox
                        marginRight='auto'
                        marginLeft='auto'
                        maxWidth={480}
                        width="calc(100vw - 68px)"
                        bgColor={linearGradient('#7030A0', '#00B0F0', 0)}
                        py={3}
                        px={3}
                        position='relative'
                        borderRadius='5rem'
                        border={`.5em solid white`}
                        boxShadow='0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19) !important'
                      >
                        <MDBox
                          left={0}
                          top='50%'
                          position='absolute'>
                          <MDButton
                            iconOnly
                            disabled={currentIndex === 0}
                            onClick={() => handleClickNavigateQuestions('left')}
                          >
                            <Icon id='icon-large-font-chevron-left'>chevron_left</Icon>
                          </MDButton>
                        </MDBox>
                        {!seeAnswer &&
                          (disabled ?
                            <MDButton
                              onClick={() => {
                                handleMessage('Quiz er allerede besvaret korrekt', 'info')
                              }}
                              sx={{
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                width: 'calc(100% - 150px)',
                                textAlign: 'center',
                                fontWeight: '400',
                                fontSize: '2em'
                              }}
                              style={{
                                border: '1px solid white',
                                color: '#ffffff',
                                backgroundColor: 'transparent',
                                position: 'absolute',
                                right: 0,
                                left: 0,
                                top: '50%',
                              }}>{givenAnswer}</MDButton> :
                            <MDInput
                              fontSize='2em'
                              className='rounded-input'
                              disabled={disabled}
                              value={givenAnswer}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGivenAnswer(e.target.value)}
                              inputProps={{ autoComplete: "off" }}
                              sx={{
                                input: {
                                  color: '#ffffff',
                                  textAlign: 'center',
                                  fontSize: '3em'
                                },
                                disabled: {
                                  backgroundColor: 'red'
                                },
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                width: 'calc(100% - 150px)',
                                textAlign: 'center',
                              }}
                              style={{
                                position: 'absolute',
                                right: 0,
                                left: 0,
                                top: '50%',
                              }}
                            />)}

                        <MDBox
                          right={0}
                          top={'50%'}
                          position='absolute'>
                          <MDButton
                            iconOnly
                            disabled={currentIndex === quizzes.length - 1}
                            onClick={() => handleClickNavigateQuestions('right')}
                          >
                            <Icon id='icon-large-font-chevron-right'>chevron_right</Icon>
                          </MDButton>
                        </MDBox>
                        <MDTypography variant='h3' color='white' fontWeight='bold' textTransform="capitalize">{title}</MDTypography>
                        <div
                          style={{
                            paddingTop: 20,
                            paddingBottom: 20,
                            width: '100%',
                            color: 'white',
                            display: 'flex',
                            marginBottom: 15,
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            paddingLeft: 15,
                            paddingRight: 15,
                            textAlign: 'start',
                            border: '1px solid #ccc'
                          }}
                          dangerouslySetInnerHTML={
                            { __html: description as string }
                          } />
                        {descriptionImageFile &&
                          <>
                            {/* <MDTypography color="white" fontWeight='bold'>Image:</MDTypography> */}
                            <Zoom>
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                  style={{
                                    maxWidth: 300,
                                    maxHeight: 300,
                                    width: "auto"
                                  }}
                                  alt="quiz img"
                                  src={descriptionImageFile}
                                />
                              </div>
                            </Zoom>
                          </>}
                        {descriptionAudioFile && <>
                          <MDTypography color="white" fontWeight='bold'>Audio:</MDTypography>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <MDButton onClick={() => handleDescriptionSound()} iconOnly><Icon fontSize='large'>{playingDescriptionSound ? 'stop' : 'play_arrow'}</Icon></MDButton>
                          </div>
                        </>}<div style={{ display: 'block', height: 300 }}>
                          {!seeAnswer ?
                            (disabled ? null :
                              <div style={{ position: 'relative' }}>
                                <div className="button" onClick={async () => {
                                  setAnswers(
                                    [...answers,
                                    {
                                      quizId: getUUIDFromKey(quiz),
                                      answerDateTime: new Date(),
                                      correct: eraseRichText(answer) === givenAnswer,
                                      answer: givenAnswer,
                                      subCategory: getUUIDFromKey(subCategory)
                                    }])
                                  handleAnswer()
                                  setSeeAnswer(true)
                                }}>
                                  <div>
                                    <MDTypography className='text' variant="h5" fontWeight="medium">
                                      <span style={{ color: '#ffffff' }}>Vis Svar</span>
                                    </MDTypography>
                                  </div>
                                </div>

                              </div>

                            )
                            : (seeAnimation ? <QuestionValidator setSeeAnimation={setSeeAnimation} correct={eraseRichText(answer) === givenAnswer} /> :
                              (
                                <>
                                  <div style={{
                                    height: "100%",
                                    marginBottom: 15,
                                    marginTop: 15,
                                    border: '1px solid #ccc',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                  }}>
                                    <div
                                      style={{
                                        fontSize: '1.2em',
                                        fontWeight: '400',
                                        pointerEvents: 'none',
                                        height: '50%',
                                        width: '100%',
                                        color: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        textAlign: 'start',
                                      }}
                                      dangerouslySetInnerHTML={
                                        { __html: answer }
                                      } />
                                    <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
                                      {answerImageFile && <Zoom >
                                        <img
                                          height='30%'
                                          style={{ maxWidth: 200, maxHeight: 145, width: "auto" }}
                                          alt="quiz img"
                                          src={answerImageFile}
                                        />
                                      </Zoom>}
                                      {answerAudioFile && <>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <MDButton onClick={() => handleAnswerSound()} iconOnly><Icon fontSize='large'>{playingAnswerSound ? 'stop' : 'play_arrow'}</Icon></MDButton>
                                        </div>
                                      </>}
                                    </div>
                                  </div>
                                </>
                              ))}
                        </div>
                        <MDBox display={'flex'} justifyContent={'center'}><img src={Logo} alt='logo' /></MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox >
      </Card >
    </DashboardLayout >
  );
}


export default ExerciseQuizzes;
